import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const BuyUGC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Buy UGC Videos | AI Generated UGC Content for Ads | Fuel72</title>
        <meta
          name="description"
          content="Buy UGC videos for your business. Get professional AI-generated user content for ads and marketing. No influencers needed. 72-hour delivery guaranteed."
        />
        <meta
          name="keywords"
          content="buy ugc, buy ugc videos, buy ugc content, ugc videos for brands, purchase ugc, order ugc videos, ugc for sale, affordable ugc, ai ugc buy"
        />
        <link rel="canonical" href="https://www.fuel72.com/buy-ugc" />
        
        {/* Hreflang */}
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/buy-ugc" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-kaufen" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/buy-ugc" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Buy UGC Videos | AI Generated Content | Fuel72" />
        <meta property="og:description" content="Buy professional UGC videos with AI. No influencers, no filming. 72-hour delivery." />
        <meta property="og:url" content="https://www.fuel72.com/buy-ugc" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fuel72.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy UGC Videos | Fuel72" />
        <meta name="twitter:description" content="Buy AI-generated UGC videos for your brand. Fast, affordable, professional." />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "AI UGC Video Pack - Buy UGC",
            "description": "Buy professional UGC videos created with AI. 6 videos + 6 posters + captions. No influencers needed.",
            "brand": { "@type": "Brand", "name": "Fuel72" },
            "offers": {
              "@type": "Offer",
              "price": "297",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "250"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does it cost to buy UGC videos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "With Fuel72, you can buy 6 professional AI UGC videos + 6 posters + captions for $297. No hidden fees, no subscriptions."
                }
              },
              {
                "@type": "Question",
                "name": "How fast can I get UGC videos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Fuel72 delivers your UGC videos in 72 hours or less. Rush delivery is also available."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need to hire influencers to buy UGC?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No! With AI UGC from Fuel72, you get professional content without hiring influencers. It's faster, cheaper, and more scalable."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen gradient-mesh flex items-center justify-center">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            Buy UGC Videos for Your{" "}
            <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
              Business
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Get professional AI-generated UGC videos without hiring influencers. 
            Fast, affordable, and designed to convert.
          </p>
          
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <h2 className="text-2xl font-bold">Why Buy UGC from Fuel72?</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>✅ <strong>No Influencers Needed</strong> - AI creates authentic UGC content</li>
              <li>✅ <strong>72-Hour Delivery</strong> - Get your videos fast</li>
              <li>✅ <strong>Affordable Pricing</strong> - $297 for 6 videos + posters</li>
              <li>✅ <strong>Ready for Ads</strong> - Optimized for Meta, TikTok, Instagram</li>
              <li>✅ <strong>Full Ownership</strong> - Use anywhere, forever</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8">What You Get When You Buy UGC</h2>
            <p className="text-muted-foreground">
              Each pack includes 6 professional AI UGC videos, 6 matching posters, 
              captions for all platforms, and one free revision round. Perfect for 
              e-commerce, local businesses, restaurants, gyms, and more.
            </p>
          </div>
          
          <p className="text-sm text-muted-foreground mt-12">Redirecting to homepage...</p>
        </div>
      </main>
    </>
  );
};

export default BuyUGC;
